// PRD 10 §2.1, §3.1, §5 — list orchestrator: filter bar, count + per-page row,
// the server-side content table, pagination, and loading/empty/error states.
//
// Slice 2 rendered the live list against the server-side query. Slice 3 (this
// file) wires the category-filter dropdown (options + counts, with auto-revert
// when the selected category disappears) and clickable column sort.
// (미분류/uncategorized filter option removed from scope — clarification ADM.010.)
// Still deferred: category manager (Slice 4), content form / row-open (Slice 5),
// and delete (Slice 7) — those props stay unwired for now.

import { useEffect, useMemo, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { FolderSearch, Info, Search } from 'lucide-react';

import { useAuthStore } from '@/app/store/auth.store';
import { Pagination } from '@/components/data/Pagination';
import { InfoModal } from '@/components/feedback/InfoModal';
import { toast } from '@/components/feedback/useToast';
import { useCan } from '@/features/roles/hooks/use-can';
import { PAGE_SIZE_OPTIONS } from '@/shared/constants/pagination';
import { useDebouncedValue } from '@/shared/hooks/use-debounced-value';
import { SEARCH_MAX, hasEmoji, normalizeSearchInput } from '@/shared/utils/search-input';

import { useCategoriesQuery, useContentsQuery } from '../hooks/use-health-encyclopedia';
import { useSetHomeExpose } from '../hooks/use-health-encyclopedia-mutations';
import { useHealthEncyclopediaPageStore } from '../store/health-encyclopedia-page.store';
import {
  MAX_HOME_EXPOSED_FALLBACK,
  type ContentListParams,
  type ContentSummary,
} from '../types/health-encyclopedia.types';
import { getEncyclopediaErrorMessage } from '../utils/error-message';
import { CategoryManagerPanel } from './CategoryManagerPanel';
import { ContentFormPanel } from './ContentFormPanel';
import { DeleteContentConfirm } from './DeleteContentConfirm';
import { EncyclopediaTable } from './EncyclopediaTable';

// PRD §3.3 — real-time search with prototype-matching 300 ms debounce.
const SEARCH_DEBOUNCE_MS = 300;

function TableSkeleton() {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8, padding: 16 }}>
      {[0, 1, 2, 3, 4].map((i) => (
        <div
          key={i}
          className="animate-pulse"
          style={{ height: 48, background: 'var(--muted)', borderRadius: 'var(--radius-sm)' }}
        />
      ))}
    </div>
  );
}

function TableError({ message, onRetry }: { message: string; onRetry: () => void }) {
  const { t } = useTranslation('health-encyclopedia');
  return (
    <div
      role="alert"
      style={{
        margin: 16,
        padding: 16,
        border: '1px solid var(--destructive)',
        borderRadius: 'var(--radius-md)',
        background: '#FEE2E8',
        color: 'var(--destructive)',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        gap: 16,
      }}
    >
      <span>{message}</span>
      <button type="button" className="btn btn-sm btn-outline" onClick={onRetry}>
        {t('list.retry')}
      </button>
    </div>
  );
}

// PRD §3.9-4 — the banner is always visible and switches copy at the cap: at 3 it
// states what the patient app shows, below 3 it states how many slots are used.
function HomeExposeNotice({ count, max }: { count: number; max: number }) {
  const { t } = useTranslation('health-encyclopedia');
  const atCap = count >= max;
  return (
    <div className="ency-home-note">
      <Info size={16} aria-hidden />
      <span>
        <Trans
          i18nKey={atCap ? 'list.homeExposeNoticeFull' : 'list.homeExposeNotice'}
          t={t}
          // `selected`, not `count` — `count` is i18next's plural selector and
          // would make resolution depend on a `_other` suffix existing.
          values={{ selected: count, max }}
          components={{ b: <b /> }}
        />
      </span>
    </div>
  );
}

function EmptyState({ title }: { title: string }) {
  return (
    <div className="table-empty">
      <i>
        <FolderSearch size={48} color="var(--muted-foreground)" />
      </i>
      <p className="table-empty__title">{title}</p>
    </div>
  );
}

export function HealthEncyclopediaView() {
  const { t } = useTranslation('health-encyclopedia');
  const isRoot = useAuthStore((s) => s.isRoot);
  // PRD §3.9 — 홈 노출 needs CONTENT_LIB:WRITE; READ sees the column read-only.
  const canWrite = useCan('CONTENT_LIB', 'WRITE');

  const search = useHealthEncyclopediaPageStore((s) => s.search);
  const setSearch = useHealthEncyclopediaPageStore((s) => s.setSearch);
  const selectedCategoryId = useHealthEncyclopediaPageStore((s) => s.selectedCategoryId);
  const setCategory = useHealthEncyclopediaPageStore((s) => s.setCategory);
  const sortKey = useHealthEncyclopediaPageStore((s) => s.sortKey);
  const sortDir = useHealthEncyclopediaPageStore((s) => s.sortDir);
  const toggleSort = useHealthEncyclopediaPageStore((s) => s.toggleSort);
  const openEdit = useHealthEncyclopediaPageStore((s) => s.openEdit);
  const page = useHealthEncyclopediaPageStore((s) => s.page);
  const perPage = useHealthEncyclopediaPageStore((s) => s.perPage);
  const setPerPage = useHealthEncyclopediaPageStore((s) => s.setPerPage);
  const setPage = useHealthEncyclopediaPageStore((s) => s.setPage);

  // Row-delete target lives in local state so the confirm modal closes cleanly
  // without threading through the page store.
  const [deleteTarget, setDeleteTarget] = useState<ContentSummary | null>(null);

  // SHCT-486 — home-exposure failures are Business Logic Errors (PRD 21 §7.1 +
  // admin-web CLAUDE.md §7.1), so they surface in a modal, not a toast. This
  // deliberately deviates from PRD 10 §3.9-3, which specified a warning toast for
  // the max-3 case; the common error-handling standard wins per requirement
  // precedence. The success messages below stay toasts (§3.9-2 — not errors).
  const [homeExposeError, setHomeExposeError] = useState<string | null>(null);
  const [pendingHomeExposeEid, setPendingHomeExposeEid] = useState<string | null>(null);
  const setHomeExposeMut = useSetHomeExpose();

  const debouncedSearch = useDebouncedValue(search, SEARCH_DEBOUNCE_MS);
  const trimmedSearch = debouncedSearch.trim();

  // SHCT-713 — [공통] 00 §4.2: emoji are blocked with an INLINE error, not by
  // silently stripping them. The typed value stays on screen so the operator can
  // see what to remove; the term is withheld from the query until it is clean,
  // otherwise the list would flip to "검색 결과가 없습니다" and read as a data
  // answer rather than a validation failure.
  const searchHasEmoji = hasEmoji(search);
  const queryableSearch = searchHasEmoji || hasEmoji(trimmedSearch) ? '' : trimmedSearch;

  const categoriesQuery = useCategoriesQuery();
  const categories = categoriesQuery.data?.categories ?? [];

  // PRD §3.2 — if the selected category was deleted (no longer in the master),
  // fall back to "전체 카테고리".
  useEffect(() => {
    if (typeof selectedCategoryId !== 'number') return;
    const result = categoriesQuery.data;
    if (result && !result.categories.some((c) => c.id === selectedCategoryId)) {
      setCategory(null);
    }
  }, [selectedCategoryId, categoriesQuery.data, setCategory]);

  // Map the <select> string value to the store's CategoryFilter shape.
  const categorySelectValue = selectedCategoryId === null ? '' : String(selectedCategoryId);

  const handleCategoryChange = (value: string) => {
    if (value === '') setCategory(null);
    else setCategory(Number(value));
  };

  const params: ContentListParams = useMemo(
    () => ({
      page,
      size: perPage,
      sortKey,
      sortDir,
      ...(selectedCategoryId !== null ? { categoryId: selectedCategoryId } : {}),
      ...(queryableSearch ? { q: queryableSearch } : {}),
    }),
    [page, perPage, sortKey, sortDir, selectedCategoryId, queryableSearch],
  );

  const { data, isLoading, isError, refetch } = useContentsQuery(params);

  const rows = data?.content ?? [];
  const totalElements = data?.totalElements ?? 0;
  const totalPages = data?.totalPages ?? 1;
  const homeExposedCount = data?.homeExposedCount ?? 0;
  // Served by the API so the screen matches the cap the server actually enforces; the
  // fallback only covers the first render, before any list response has arrived.
  const maxHomeExposed = data?.maxHomeExposed ?? MAX_HOME_EXPOSED_FALLBACK;

  // PRD §3.9-2 — success feedback is a toast; failures go to the modal above.
  // The row toggle is optimistic-free: the switch only moves once the server has
  // confirmed, because the count it feeds is server-owned.
  const handleHomeExposeChange = (content: ContentSummary, next: boolean) => {
    setPendingHomeExposeEid(content.eid);
    setHomeExposeMut.mutate(
      { eid: content.eid, homeExposed: next },
      {
        onSuccess: () => {
          toast.success(next ? t('list.homeExposeOn') : t('list.homeExposeOff'));
        },
        onError: (e) => {
          setHomeExposeError(
            getEncyclopediaErrorMessage(e, t, 'list.homeExposeError'),
          );
        },
        onSettled: () => setPendingHomeExposeEid(null),
      },
    );
  };

  // PRD §3.1–3.3 / §5 — pick the empty-state copy by why the list is empty.
  const emptyTitle = queryableSearch
    ? t('list.emptyNoMatch')
    : selectedCategoryId !== null
      ? t('list.emptyCategory')
      : t('list.empty');

  return (
    <>
      <div className="filter-bar">
        {/* PRD §2.2 #5 / §3.2 — category filter (left of search). Options carry
            per-category counts. (미분류 option removed from scope — ADM.010.) */}
        <select
          className="filter-bar__select"
          style={{ flex: 'none', minWidth: 180 }}
          value={categorySelectValue}
          aria-label={t('list.categoryFilterAll')}
          onChange={(e) => handleCategoryChange(e.target.value)}
        >
          <option value="">{t('list.categoryFilterAll')}</option>
          {categories.map((category) => (
            <option key={category.id} value={String(category.id)}>
              {t('list.categoryFilterOption', {
                name: category.name,
                count: category.contentCount,
              })}
            </option>
          ))}
        </select>
        <div className="filter-bar__search" style={{ flex: 1 }}>
          <input
            type="text"
            className="filter-bar__search-input"
            value={search}
            // SHCT-713 — [공통] 00 §3 SEARCH class: 1~50자. Hard cap, matching how
            // every other field on this screen is capped (제목 100 / 카테고리명 20 /
            // 영상 제목 50). `normalizeSearchInput` also folds pasted newlines to a
            // space (§4.2) — `maxLength` alone does not survive a paste.
            maxLength={SEARCH_MAX}
            onChange={(e) => setSearch(normalizeSearchInput(e.target.value))}
            placeholder={t('list.searchPlaceholder')}
            aria-label={t('list.searchPlaceholder')}
            aria-invalid={searchHasEmoji || undefined}
            aria-describedby={searchHasEmoji ? 'encyclopedia-search-error' : undefined}
          />
          <Search size={16} className="filter-bar__search-icon" />
          {searchHasEmoji && (
            <p
              id="encyclopedia-search-error"
              className="form-field__error"
              style={{ position: 'absolute', top: '100%', left: 0, marginTop: 4 }}
            >
              {t('list.searchEmojiNotAllowed')}
            </p>
          )}
        </div>
      </div>

      {/* PRD §3.9-4 / prototype — banner sits between the filter bar and the
          count row. Shown for every role that can see the list; it describes a
          library-wide state, not an action. */}
      <HomeExposeNotice count={homeExposedCount} max={maxHomeExposed} />

      <div
        style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 'var(--space-4)' }}
      >
        <span style={{ fontSize: 13, color: 'var(--muted-foreground)' }}>
          {t('list.count', { count: totalElements })}
        </span>
        {/* PRD §2.2 #8 — 목록 표시 개수 선택. SHCT-715: the select used to carry a
            single option and a no-op onChange, so it could not select anything. */}
        <select
          className="filter-bar__select"
          style={{ marginLeft: 'auto' }}
          value={perPage}
          aria-label={t('list.itemsPerPage', { count: perPage })}
          onChange={(e) => setPerPage(Number(e.target.value))}
        >
          {PAGE_SIZE_OPTIONS.map((n) => (
            <option key={n} value={n}>
              {t('list.itemsPerPage', { count: n })}
            </option>
          ))}
        </select>
      </div>

      <div className="card">
        {isLoading ? (
          <TableSkeleton />
        ) : isError ? (
          <TableError message={t('list.loadError')} onRetry={() => void refetch()} />
        ) : (
          <>
            {/* Prototype parity: the table header stays mounted on empty results;
                the empty notice renders as a sibling below the empty tbody. */}
            <EncyclopediaTable
              rows={rows}
              sortKey={sortKey}
              sortDir={sortDir}
              canDelete={isRoot}
              canWrite={canWrite}
              homeExposedCount={homeExposedCount}
              maxHomeExposed={maxHomeExposed}
              pendingHomeExposeEid={pendingHomeExposeEid}
              onSort={toggleSort}
              onRowOpen={openEdit}
              onDelete={setDeleteTarget}
              onHomeExposeChange={handleHomeExposeChange}
            />
            {rows.length === 0 && <EmptyState title={emptyTitle} />}
          </>
        )}
      </div>

      <Pagination page={page} totalPages={totalPages} onPageChange={setPage} />

      <CategoryManagerPanel />
      <ContentFormPanel />
      <DeleteContentConfirm content={deleteTarget} onClose={() => setDeleteTarget(null)} />

      <InfoModal
        open={homeExposeError !== null}
        title={t('list.homeExposeErrorTitle')}
        body={homeExposeError ?? ''}
        onClose={() => setHomeExposeError(null)}
      />
    </>
  );
}
